import React, { useMemo, useState } from 'react'
import Icon from '@mdi/react'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import { SETTINGS_DCMHEADER } from '../constants/settings'
import { toCsv } from '../functions'
import {
  mdiClose,
  mdiContentSaveOutline,
  mdiFileDocument,
  mdiMagnify,
  mdiCloseCircle,
} from '@mdi/js'
import {
  dumpDicomDataset,
  GROUP_ORDER,
} from '../utils/dicomDatasetDump'
import { readTransferSyntaxFromDataSet, resolveTransferSyntax } from '../utils/transferSyntax'
import '../skins/dicom-header.css'

const panelBg = '#1a1d21'
const rowBorder = 'rgba(255,255,255,0.06)'
const textMuted = 'rgba(255,255,255,0.55)'
const textMain = 'rgba(255,255,255,0.92)'
const iconOn = '#FFFFFF'

const SKIN_ACCENT = {
  classic: '#4DD0E1',
  edge: '#90CAF9',
  studio: '#C4A574',
}

function MetaRow({ name, value, id, vr, depth }) {
  const pad = depth ? 10 + depth * 12 : 12
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(110px, 42%) minmax(0, 1fr)',
        gap: '2px 10px',
        padding: `6px 12px 6px ${pad}px`,
        borderBottom: `1px solid ${rowBorder}`,
        alignItems: 'start',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: textMain,
            lineHeight: 1.3,
            wordBreak: 'break-word',
          }}
        >
          {name}
        </div>
        <div
          style={{
            fontSize: 9,
            color: textMuted,
            marginTop: 2,
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            letterSpacing: '0.02em',
          }}
        >
          {id}
          {vr ? ` · ${vr}` : ''}
        </div>
      </div>
      <div
        style={{
          fontSize: 12,
          color: textMain,
          lineHeight: 1.4,
          wordBreak: 'break-word',
          fontFamily:
            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          opacity: value ? 1 : 0.45,
        }}
        title={value}
      >
        {value || '—'}
      </div>
    </div>
  )
}

function MetaSection({ title, rows, accent, sectionBg }) {
  if (!rows || !rows.length) return null
  return (
    <section style={{ marginBottom: 10 }}>
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          padding: '8px 12px 6px',
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: accent,
          background: `linear-gradient(180deg, ${sectionBg} 70%, transparent)`,
        }}
      >
        {title}
        <span style={{ color: textMuted, fontWeight: 500, marginLeft: 6 }}>
          {rows.length}
        </span>
      </div>
      <div
        style={{
          margin: '0 8px',
          borderRadius: 10,
          border: '1px solid rgba(255,255,255,0.08)',
          background: 'rgba(36,40,48,0.85)',
          overflow: 'hidden',
        }}
      >
        {rows.map((r) => (
          <MetaRow
            key={r.path || `${r.tag}-${r.depth}`}
            name={r.name}
            value={r.value}
            id={r.id}
            vr={r.vr}
            depth={r.depth || 0}
          />
        ))}
      </div>
    </section>
  )
}

function previewRows(image, dcmViewer) {
  const rows = []
  const push = (name, value, tag) => {
    if (value == null || value === '') return
    rows.push({
      tag: tag || name,
      id: tag || '',
      name,
      vr: '',
      value: String(value),
      group: 'Render',
      depth: 0,
      path: name,
      search: `${name} ${value}`.toLowerCase(),
    })
  }
  push('Modo', 'PREVIEW (sin dataset DICOM)')
  if (image) {
    push('Rows', image.rows || image.height)
    push('Columns', image.columns || image.width)
    push('Window Center', image.windowCenter)
    push('Window Width', image.windowWidth)
  }
  if (dcmViewer) {
    push('Transfer Syntax', dcmViewer.getTransferSyntax && dcmViewer.getTransferSyntax())
    push('SOP Class', dcmViewer.getSopClass && dcmViewer.getSopClass())
    push('SOP Instance UID', dcmViewer.getSopInstanceUID && dcmViewer.getSopInstanceUID())
  }
  return rows
}

function groupRows(flat) {
  const byGroup = {}
  flat.forEach((r) => {
    const g = r.group || 'Otros'
    if (!byGroup[g]) byGroup[g] = []
    byGroup[g].push(r)
  })
  const known = GROUP_ORDER.filter((g) => byGroup[g])
  const rest = Object.keys(byGroup)
    .filter((g) => !GROUP_ORDER.includes(g))
    .sort()
  return [...known, ...rest].map((g) => ({ title: g, rows: byGroup[g] }))
}

/**
 * Panel de metadatos DICOM — dump completo + búsqueda en vivo.
 */
const DicomHeader = ({ dcmViewer, color = iconOn, onClose, skin = 'classic' }) => {
  const [query, setQuery] = useState('')

  const image = dcmViewer && dcmViewer.image
  const dataSet = image && image.data
  const isPreview = !dataSet || !dataSet.elements

  const allRows = useMemo(() => {
    try {
      if (isPreview) return previewRows(image, dcmViewer)
      return dumpDicomDataset(dataSet)
    } catch (e) {
      return [
        {
          tag: 'error',
          id: '',
          name: 'Error',
          vr: '',
          value: e.message || String(e),
          group: 'Error',
          depth: 0,
          path: 'error',
          search: 'error',
        },
      ]
    }
  }, [dataSet, dcmViewer, image, isPreview])

  const transferSyntax = useMemo(() => {
    if (!isPreview) return readTransferSyntaxFromDataSet(dataSet)
    const raw =
      dcmViewer &&
      dcmViewer.getTransferSyntax &&
      String(dcmViewer.getTransferSyntax() || '')
    const uidMatch = raw && raw.match(/^(1\.2\.840\.10008\.[^\s\[]+)/)
    return uidMatch ? resolveTransferSyntax(uidMatch[1]) : null
  }, [dataSet, dcmViewer, isPreview])

  const filtered = useMemo(() => {
    const q = String(query || '')
      .trim()
      .toLowerCase()
    if (!q) return allRows
    const tokens = q.split(/\s+/).filter(Boolean)
    return allRows.filter((r) => tokens.every((t) => (r.search || '').includes(t)))
  }, [allRows, query])

  const sections = useMemo(() => groupRows(filtered), [filtered])

  const saveHeader = () => {
    let exportAs = localStorage.getItem(SETTINGS_DCMHEADER)
    if (exportAs === null) {
      exportAs = 'json'
      localStorage.setItem(SETTINGS_DCMHEADER, exportAs)
    }

    const exportRows = filtered.length ? filtered : allRows
    let fileData = ''
    if (exportAs === 'csv') {
      fileData = toCsv(
        exportRows.map((r) => ({
          tag: r.id,
          name: r.name,
          vr: r.vr,
          value: r.value,
          group: r.group,
        }))
      )
    } else {
      fileData = JSON.stringify(
        exportRows.map((r) => ({
          tag: r.id,
          name: r.name,
          vr: r.vr,
          value: r.value,
          group: r.group,
        })),
        null,
        2
      )
    }

    const element = document.createElement('a')
    const file = new Blob([fileData], { type: 'text/plain' })
    element.href = URL.createObjectURL(file)
    element.download = `${(dcmViewer.localfile && dcmViewer.localfile.name) || 'header'}.${exportAs}`
    document.body.appendChild(element)
    element.click()
    document.body.removeChild(element)
  }

  const accent = SKIN_ACCENT[skin] || SKIN_ACCENT.classic
  const isStudio = skin === 'studio'
  const isEdge = skin === 'edge'
  const headBorder = isStudio
    ? '1px solid rgba(196,165,116,0.18)'
    : isEdge
      ? '1px solid rgba(100,181,246,0.28)'
      : '1px solid rgba(255,255,255,0.08)'
  const sectionBg = isEdge || isStudio ? 'transparent' : panelBg
  const hasFilter = String(query).trim().length > 0

  return (
    <div
      className={`udv-dicom-header udv-dicom-header--${skin}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        background: isEdge || isStudio ? 'transparent' : panelBg,
        color: textMain,
        boxSizing: 'border-box',
      }}
    >
      <header
        style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 8px 8px 12px',
          borderBottom: headBorder,
          background: 'rgba(0,0,0,0.25)',
        }}
      >
        <Icon path={mdiFileDocument} size="1.05rem" color={accent} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: '0.02em',
              lineHeight: 1.2,
            }}
          >
            Metadatos
          </div>
          <div style={{ fontSize: 10, color: textMuted, marginTop: 2 }}>
            {isPreview
              ? 'Vista previa · sin dataset'
              : hasFilter
                ? `${filtered.length} de ${allRows.length} tags`
                : `${allRows.length} tags del dataset`}
          </div>
        </div>
        <Tooltip title="Exportar (lista filtrada)">
          <IconButton
            size="small"
            onClick={saveHeader}
            aria-label="Exportar"
            style={{ color: iconOn }}
          >
            <Icon path={mdiContentSaveOutline} size="1.15rem" color={color || iconOn} />
          </IconButton>
        </Tooltip>
        {typeof onClose === 'function' ? (
          <Tooltip title="Cerrar">
            <IconButton
              size="small"
              onClick={onClose}
              aria-label="Cerrar metadatos"
              style={{ color: iconOn }}
            >
              <Icon path={mdiClose} size="1.15rem" color={iconOn} />
            </IconButton>
          </Tooltip>
        ) : null}
      </header>

      <div
        style={{
          flexShrink: 0,
          padding: '8px 10px 6px',
          borderBottom: headBorder,
        }}
      >
        {transferSyntax && transferSyntax.uid ? (
          <div
            style={{
              marginBottom: 8,
              padding: '8px 10px',
              borderRadius: 8,
              border: `1px solid ${transferSyntax.kindColor}55`,
              background: 'rgba(255,255,255,0.04)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: transferSyntax.kindColor,
                  padding: '2px 7px',
                  borderRadius: 4,
                  border: `1px solid ${transferSyntax.kindColor}88`,
                  background: `${transferSyntax.kindColor}18`,
                }}
              >
                {transferSyntax.kindLabel}
              </span>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: textMain,
                }}
              >
                {transferSyntax.short}
              </span>
            </div>
            <div
              style={{
                marginTop: 5,
                fontSize: 10,
                color: textMuted,
                lineHeight: 1.35,
                wordBreak: 'break-word',
              }}
              title={transferSyntax.detail}
            >
              {transferSyntax.name}
            </div>
            <div
              style={{
                marginTop: 3,
                fontSize: 9,
                color: textMuted,
                fontFamily:
                  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                wordBreak: 'break-all',
              }}
            >
              {transferSyntax.uid}
            </div>
          </div>
        ) : null}
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 10px',
            borderRadius: 8,
            background: 'rgba(255,255,255,0.06)',
            border: `1px solid ${hasFilter ? accent : 'rgba(255,255,255,0.1)'}`,
          }}
        >
          <Icon path={mdiMagnify} size="0.95rem" color={hasFilter ? accent : textMuted} />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar tag, nombre o valor…"
            autoComplete="off"
            spellCheck={false}
            style={{
              flex: 1,
              minWidth: 0,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              color: textMain,
              fontSize: 13,
              lineHeight: 1.4,
            }}
          />
          {hasFilter ? (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Limpiar búsqueda"
              style={{
                border: 'none',
                background: 'transparent',
                padding: 0,
                cursor: 'pointer',
                display: 'flex',
                color: textMuted,
              }}
            >
              <Icon path={mdiCloseCircle} size="0.95rem" color={textMuted} />
            </button>
          ) : null}
        </label>
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          padding: '8px 0 16px',
        }}
      >
        {sections.map((s) => (
          <MetaSection
            key={s.title}
            title={s.title}
            rows={s.rows}
            accent={accent}
            sectionBg={sectionBg}
          />
        ))}
        {!sections.length ? (
          <div
            style={{
              padding: 24,
              textAlign: 'center',
              color: textMuted,
              fontSize: 12,
            }}
          >
            {hasFilter
              ? 'Ningún tag coincide con la búsqueda'
              : 'Sin metadatos disponibles'}
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default DicomHeader
